import { useQuery } from '@tanstack/react-query'
import { ProjectService } from 'src/services/project.service'
import { ErrorQueryHandler } from 'src/components/error/error-query-handler.component'
import { useCurrentProject } from 'src/providers/current-project.provider'

import {
  TProgrammedMessageFormValues,
  UpdateProgrammedMessageForm,
} from '../_components/programmed-message-form.component'
import { ConfigurationHeaderWrapper } from '../../../../_components/configuration-header-wrapper.component'
import { useTranslation } from 'react-i18next'
import { useEffect } from 'react'
import { useLocalizedNavigate } from 'src/hooks/use-localized-navigate'
import { useParams } from 'react-router-dom'
import { FollowupTimeline } from '../../_components/followup-timeline.component'
import { useSortedFollowupTimeline } from '../../_components/use-sorted-followup-timeline.hook'
import { useNotification } from 'src/providers/notification.provider'
import { getNotEditableReasonLabel } from '../_components/not-editable-message.helper'

export default function UpdateProgrammedMessagePage() {
  const { t } = useTranslation()
  const { messageId: messageIdParam, followupId } = useParams<{
    messageId: string
    followupId: string
  }>()
  const messageId = Number(messageIdParam)
  const { project } = useCurrentProject()
  const navigate = useLocalizedNavigate()
  const { notify } = useNotification()

  const {
    isLoading,
    error,
    data: message,
  } = useQuery({
    queryKey: ['programmed-message', project.id, messageId],
    queryFn: () => ProjectService.getSelfConfigurationMessage(project.id, messageId),
  })

  const {
    isLoading: isLoadingTimeline,
    error: errorTimeline,
    timelineMedEventsSorted,
  } = useSortedFollowupTimeline(followupId)

  useEffect(() => {
    if (message?.id && !message.isEditable) {
      notify({
        title: t('pages.configuration.messages.update-programmed-message'),
        message: getNotEditableReasonLabel(t, message.notEditableReason),
        appearance: 'ERROR',
      })
      navigate(`/${project.id}/configuration/followups/${followupId}`, { replace: true })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [message?.id, message?.isEditable])

  if (error || errorTimeline) return <ErrorQueryHandler error={error || errorTimeline} />
  if (isLoading || !message || isLoadingTimeline) return <div className="loading" />
  if (!message.followupId) return null

  const defaultValues: TProgrammedMessageFormValues = {
    followupId: message.followupId,
    medEventIds: message.medEventIds,
    schedule: {
      durationDiff: message.schedule.durationDiff,
      atTime: message.schedule?.atTime,
      avoidedWeekdays: message.schedule?.avoidedWeekdays,
      avoidPublicHolidays: message.schedule?.avoidPublicHolidays,
      avoidedDates: message.schedule?.avoidedDates,
      gracePeriodDuration: message.schedule?.gracePeriodDuration,
      preventRescheduleOnceTriggered: message.schedule?.preventRescheduleOnceTriggered,
    },
    messageName: message.messageName,
    profileRules: message.profileRules,
    categories: message.categories ?? [],
    humanReviewCheck: message.humanReviewCheck ?? null,
  }

  return (
    <ConfigurationHeaderWrapper
      title={t('pages.configuration.messages.update-programmed-message')}
      className="c-flex c-flex-row c-pb-10 c-relative"
    >
      <FollowupTimeline timelineMedEventsSorted={timelineMedEventsSorted} />
      <div className="c-bg-grey-200 c-w-px c-h-full" />
      <div className="c-w-full c-min-w-0 c-pl-4">
        <UpdateProgrammedMessageForm
          defaultValues={defaultValues}
          messageId={messageId}
          followupId={followupId!}
        />
      </div>
    </ConfigurationHeaderWrapper>
  )
}
